import Table from '@shared/components/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import IndentificatorForm from './IndentificatorForm';
import IndentificatorSecondForm from './IdentificatorSecondForm';
import useIdentificators from '@modules/users/hooks/useIdentificators';
import DeleteModal from '@shared/components/DeleteModal';
import { identificatorsService } from '@modules/additionalSettings/nomenclatures/services/identificators';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useNavigate } from 'react-router-dom';
import { useReportGenerator } from '@shared/hooks/useReportGenerator';

export default function Nomenclatures() {
    const { t } = useTranslation();
    const [selectedRecord1, setSelectedRecord1] = useState(null);
    const [selectedRecord2, setSelectedRecord2] = useState(null);
    const [modalState1, setModalState1] = useState({ visible: false, type: null });
    const [modalState2, setModalState2] = useState({ visible: false, type: null });
    const navigate = useNavigate();
    const {
        data: data1,
        loading,
        error,
        pageNumber,
        totalPages,
        totalElements,
        pageSize,
        searchQuery,
        fetchIdentificators,
        handleSearchIdentificators,
        handleSortIdentificators,
        setpageNumber,
        setPageSize,
        setSearchQuery,
        nomstructData,
        nomstructLoading,
        nomstructError,
        fetchNomstruct
    } = useIdentificators();

    // Report generator for all determs
    const {
        generatePdfReport: generateAllPdfReport,
        generateExcelReport: generateAllExcelReport,
        pdfLoading: allPdfLoading,
        excelLoading: allExcelLoading
    } = useReportGenerator({
        title: t('additionalSettings.identificators.listAll'),
        columns: [
            // { key: 'hardcodedKey', label: 'Ключ', render: () => 'Номкл' },
            { key: 'mnemonic', label: t('additionalSettings.identificators.table.headers.code') },
            { key: 'note', label: t('additionalSettings.identificators.table.headers.title') },
            { key: 'typeDet', label: t('additionalSettings.identificators.table.headers.type'), render: () => 'Номкл' },
            { key: 'sizeDet', label: t('additionalSettings.identificators.table.headers.value') },
            { key: "readOnly", label: t('common.readOnly') },
            { key: "nomStructs", label: t('common.nomStructs') },
        ],
        fileName: 'determs_report',
        companyName: '',
        reportService: async () => {
            const params = { searchQuery };
            const data = await identificatorsService.getDetermsReportData(params);
            // Transform the data to show Yes/No for readOnly field and nomStructs
            return data.map(item => ({
                ...item,
                readOnly: item.readOnly ? t('common.yes') : t('common.no'),
                nomStructs: (Array.isArray(item.nomStructs) && item.nomStructs.length > 0)
                    ? item.nomStructs.map(ns => ns.name).join('\n')
                    : t('additionalSettings.identificators.form.linear')
            }));
        },
        customStyles: {
            nomStructs: { 
                cellWidth: 'auto',
                whiteSpace: 'pre-line'
            }
        }
    });

    // Report generator for selected nomenclature
    const {
        generatePdfReport: generateSelectedPdfReport,
        generateExcelReport: generateSelectedExcelReport,
        pdfLoading: selectedPdfLoading,
        excelLoading: selectedExcelLoading
    } = useReportGenerator({
        title: t('additionalSettings.identificators.listSelected'),
        columns: [
            { key: 'value', label: t('additionalSettings.identificators.table.headers.code') },
            { key: 'note', label: t('additionalSettings.identificators.table.headers.title') },
            { key: "active", label: t('additionalSettings.identificators.table.headers.readOnly') }
        ],
        fileName: 'nomenclature_report',
        companyName: selectedRecord1?.note || '',
        reportService: async () => {
            if (!selectedRecord1) {
                throw new Error(t('common.selectRecord'));
            }
            const data = await identificatorsService.getNomenclaturesReportData(selectedRecord1.id);
            // Transform the data to show Yes/No for active field
            return data.map(item => ({
                ...item,
                active: item.active ? t('common.yes') : t('common.no')
            }));
        },
        customStyles: {}
    });

    // Fetch data on component mount
    useEffect(() => {
        fetchIdentificators();
    }, []);

    // Refetch data when pagination parameters change
    useEffect(() => {
        fetchIdentificators({
            searchQuery,
            sortDir: 'asc'
        });
    }, [pageNumber, pageSize]);

    const handleOpenModal1 = (type) => {
        setModalState1({ visible: true, type });
    };

    const handleCloseModal1 = () => {
        setModalState1({ visible: false, type: null });
        setSelectedRecord1(null);
    };

    const handleOpenModal2 = (type) => {
        setModalState2({ visible: true, type });
    };

    const handleCloseModal2 = () => {
        setModalState2({ visible: false, type: null });
    };

    const handleSuccess = () => {
        fetchIdentificators({
            searchQuery,
            sortDir: 'asc'
        });
    };

    const handleConfirmDelete = async () => {
        if (!selectedRecord1) {
            notifyError('No record selected for deletion');
            return;
        }

        try {
            await identificatorsService.deleteDeterm(selectedRecord1.id);
            notifySuccess(t('common.deletedSuccessfully'));
            handleSuccess();
            handleCloseModal1();
        } catch (error) {
            console.error('Error deleting determ:', error);
            notifyError(error?.response?.data?.message || t('common.errorMessage'));
        }
    };

    const handleConfirmDelete2 = async () => {
        if (!selectedRecord2) {
            notifyError('No record selected for deletion');
            return;
        }

        try {
            await identificatorsService.deleteNomstruct(selectedRecord2.id);
            notifySuccess(t('common.deletedSuccessfully'));

            // Refetch the nomstruct data for the currently selected determ
            if (selectedRecord1 && selectedRecord1.id) {
                await fetchNomstruct(selectedRecord1.id);
            }

            handleSuccess();
            handleCloseModal2();
        } catch (error) {
            console.error('Error deleting nomstruct:', error);
            notifyError(error?.response?.data?.message || t('common.errorMessage'));
        }
    };

    const columns1 = [

        {
            key: 'typeDet', label: t('additionalSettings.identificators.table.headers.type'),
        },
        {
            key: 'mnemonic', label: t('additionalSettings.identificators.table.headers.code'),
        },
        {
            key: 'sizeDet', label: t('additionalSettings.identificators.table.headers.value'),
        },
        {
            key: 'note', label: t('additionalSettings.identificators.table.headers.title'),
        },
    ];

    const columns2 = [
        {
            key: 'partsize', label: 'Value',
        },
        {
            key: 'name', label: 'Title',
        }
    ];

    // Get nomstruct data for second table
    const getData2 = () => {
        return nomstructData || [];
    };

    const handleCheckboxChange1 = (index) => {
        const selectedItem = data1[index];
        setSelectedRecord1(selectedItem);
        setSelectedRecord2(null); // Reset second table selection when first table selection changes

        // Fetch nomstruct data for the selected determ
        if (selectedItem && selectedItem.id && selectedItem.typeDet === true) {
            fetchNomstruct(selectedItem.id);
        } else {
            // Clear nomstruct data if no item is selected
            fetchNomstruct(null);
        }
    };

    const handleCheckboxChange2 = (index) => {
        const currentData2 = getData2();
        const selectedItem = currentData2[index];
        setSelectedRecord2(selectedItem);
    };

    const actionButtons1 = (
        <>
            <button
                className="btn btn-primary"
                title={t('common.values')}
                onClick={() => {
                    if (selectedRecord1) {
                        localStorage.setItem('selectedDetermId', selectedRecord1.id);
                        localStorage.setItem('selectedDetermMnemonic', selectedRecord1.mnemonic);
                        localStorage.setItem('selectedDetermTypeDet', selectedRecord1.typeDet);
                        navigate('/additional-settings/nomenclatures/values');
                    } else {
                        notifyError(t('common.selectRecord'));
                    }
                }}
            >
                {t('common.values')}
            </button>
            <button
                className="btn btn-primary"
                onClick={generateAllPdfReport}
                disabled={allPdfLoading}
            >
                {allPdfLoading ? (
                    <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Генериране на PDF...
                    </>
                ) : (
                    t('additionalSettings.identificators.listAll')
                )}
            </button>
            <button
                className="icon-button"
                onClick={() => handleOpenModal1('add')}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>

            {selectedRecord1?.readOnly === false && (
                <>
                    <button
                        className={`icon-button ${!selectedRecord1 ? 'disabled-link' : ''}`}
                        disabled={!selectedRecord1}
                        onClick={() => handleOpenModal1('edit')}
                        title={t('common.edit')}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                </>
            )}
            {selectedRecord1?.readOnly === false && (
                <>
                    {selectedRecord1?.readOnly === false && (
                        <button
                            className={`icon-button ${!selectedRecord1 ? 'disabled-link' : ''}`}
                            disabled={!selectedRecord1}
                            onClick={() => handleOpenModal1('delete')}
                            title={t('common.delete')}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </>
            )}
        </>
    );


            const actionButtons2 = (
            <>
                <button
                    className={`btn btn-primary ${!selectedRecord1 ? 'disabled-link' : ''}`}
                    disabled={!selectedRecord1 || selectedPdfLoading}
                    onClick={generateSelectedPdfReport}
                >
                    {selectedPdfLoading ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Генериране на PDF...
                        </>
                    ) : (
                        t('additionalSettings.identificators.listSelected')
                    )}
                </button>
                <button
                    className={`icon-button ${!selectedRecord1 ? 'disabled-link' : ''}`}
                    disabled={!selectedRecord1}
                    onClick={() => handleOpenModal2('add')}
                    title={t('common.add')}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>

                <button
                    className={`icon-button ${!selectedRecord2 ? 'disabled-link' : ''}`}
                    disabled={!selectedRecord2}
                    onClick={() => handleOpenModal2('edit')}
                    title={t('common.edit')}
                >
                    <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                    className={`icon-button ${!selectedRecord2 ? 'disabled-link' : ''}`}
                    disabled={!selectedRecord2}
                    onClick={() => handleOpenModal2('delete')}
                    title={t('common.delete')}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            </>
            );

    // Transform API data to match table format
    const transformedData1 = data1.map(item => ({
                ...item,
                typeDet: item.typeDet ? t('additionalSettings.identificators.form.tree') : t('additionalSettings.identificators.form.linear'),
                readOnly: item.readOnly ? t('common.yes') : t('common.no'),
                nomStructs: (Array.isArray(item.nomStructs) && item.nomStructs.length > 0) 
                    ? item.nomStructs.map(ns => ns.name).join('\n')
                    : t('additionalSettings.identificators.form.linear'),
            active: item.active ? 'Yes' : 'No'
    }));

            return (
            <>
                <div className="large-table width-60 me-2">
                    <Table
                        title={t('additionalSettings.identificators.title')}
                        columns={columns1}
                        data={transformedData1}
                        loading={loading}
                        showPageSizeSelector={true}
                        showPagination={true}
                        showCustomSearch={true}
                        actionButtons={actionButtons1}
                        handleCheckboxChange={handleCheckboxChange1}
                        selectedData={selectedRecord1?.id}
                        pageNumber={pageNumber}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        pageSize={pageSize}
                        setpageNumber={setpageNumber}
                        setPageSize={setPageSize}
                        handleSearchClick={handleSearchIdentificators}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleSort={handleSortIdentificators}
                    />
                </div>
                <div className="small-table width-35 ms-2">
                    <Table
                        title={t('additionalSettings.identificators.levels')}
                        columns={columns2}
                        data={getData2()}
                        loading={nomstructLoading}
                        showPageSizeSelector={false}
                        showPagination={false}
                        showPreviousPage={false}
                        showCustomSearch={true}
                        actionButtons={actionButtons2}
                        handleCheckboxChange={handleCheckboxChange2}
                        selectedData={selectedRecord2?.id}
                    />
                </div>

                {/* Add/Edit Modal */}
                {modalState1.visible && (modalState1.type === 'add' || modalState1.type === 'edit') && (
                    <IndentificatorForm
                        onClose={handleCloseModal1}
                        determ={modalState1.type === 'edit' ? selectedRecord1 : null}
                        modalType={modalState1.type}
                        onSuccess={handleSuccess}
                    />
                )}

                {/* Delete Modal */}
                {modalState1.visible && modalState1.type === 'delete' && (
                    <DeleteModal
                        onClose={handleCloseModal1}
                        onConfirm={handleConfirmDelete}
                        selectedData={selectedRecord1?.id}
                        name={selectedRecord1?.mnemonic}
                        entityType="record"
                    />
                )}

                {/* Add/Edit Modal for Second Table */}
                {modalState2.visible && (modalState2.type === 'add' || modalState2.type === 'edit') && (
                    <IndentificatorSecondForm
                        onClose={handleCloseModal2}
                        nomstruct={modalState2.type === 'edit' ? selectedRecord2 : null}
                        modalType={modalState2.type}
                        onSuccess={handleSuccess}
                        selectedDeterm={selectedRecord1}
                        fetchNomstruct={fetchNomstruct}
                    />
                )}

                {/* Delete Modal for Second Table */}
                {modalState2.visible && modalState2.type === 'delete' && (
                    <DeleteModal
                        onClose={handleCloseModal2}
                        onConfirm={handleConfirmDelete2}
                        selectedData={selectedRecord2?.id}
                        name={selectedRecord2?.name}
                        entityType="record"
                    />
                )}
            </>
            );
}